import { Input, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';

import type { RoomOption } from './types';

// Table cells that edit an Olympiad Participant's Exam room and Exam number in
// place. A click inside never reaches the row (which opens the drawer).
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export function RoomCell({
  value,
  rooms,
  disabled,
  onSave,
}: {
  value: number | null;
  rooms: RoomOption[];
  disabled: boolean;
  onSave: (roomId: number | null) => Promise<boolean>;
}) {
  const [saving, setSaving] = useState(false);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the click off the row
    <div onClick={stop} onKeyDown={stop}>
      <Select
        size="small"
        variant="filled"
        style={{ width: '100%' }}
        placeholder="Өрөө"
        allowClear
        showSearch
        optionFilterProp="label"
        popupMatchSelectWidth={false}
        disabled={disabled || saving}
        loading={saving}
        value={value ?? undefined}
        options={rooms.map((r) => ({ value: r.id, label: r.label }))}
        onChange={async (v) => {
          setSaving(true);
          await onSave(v ?? null);
          setSaving(false);
        }}
      />
    </div>
  );
}

// Saves on Enter or when focus leaves; Enter also moves to the next row's
// number, so a column of numbers can be typed in one go. Escape undoes.
export function ExamNumberCell({
  participantId,
  value,
  disabled,
  onSave,
  onNext,
}: {
  participantId: number;
  value: string | null;
  disabled: boolean;
  onSave: (examNumber: string | null) => Promise<boolean>;
  onNext: () => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  // What the server holds: Enter saves, then the blur that follows must not save again.
  const saved = useRef(value ?? '');

  useEffect(() => {
    saved.current = value ?? '';
    setDraft(value ?? '');
  }, [value]);

  const commit = async () => {
    const next = draft.trim();
    if (next === saved.current) return true;
    setSaving(true);
    const ok = await onSave(next || null);
    setSaving(false);
    if (ok) saved.current = next;
    else setDraft(saved.current);
    return ok;
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the click off the row
    <div onClick={stop} onKeyDown={stop}>
      <Input
        size="small"
        variant="filled"
        placeholder="Дугаар"
        data-exam-number={participantId}
        disabled={disabled || saving}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={async (e) => {
          e.stopPropagation();
          if (e.key === 'Escape') {
            setDraft(saved.current);
            e.currentTarget.blur();
          } else if (e.key === 'Enter') {
            if (await commit()) onNext();
          }
        }}
      />
    </div>
  );
}
